import './publicPath'; // the public path MUST be imported first!
import 'whatwg-fetch'; // For IE11 and older
import 'url-search-params-polyfill'; // For IE and Safari 10 and older
import 'focus-options-polyfill';
import React from 'react';

import {AngelouTermSheet} from '@newsela/angelou';
import lscache from 'lscache';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import ReactDOM from 'react-dom';
import {QueryClient, QueryClientProvider} from 'react-query';
import {Provider} from 'react-redux';
import {Router, Route} from 'react-router';
import {QueryParamProvider} from 'use-query-params';

import GlobalLayout from 'components/GlobalLayout';
import {fetchCollections} from 'modulesV2/actions/collectionActions';
import {getLikedContent} from 'modulesV2/actions/likeContentActions';
import {setScreenSize} from 'modulesV2/actions/ScreenSize';
import AssignButtonModal from 'static/three-oh/src/components/AssignButton/AssignButtonModal';
import {SalesforceChatSupport} from 'static/three-oh/src/components/SalesforceChatSupport';
import AppcuesTrackingContainer from 'static/three-oh/src/containers/AppcuesTrackingContainer/AppcuesTrackingContainer';
import HeapTrackingContainer from 'static/three-oh/src/containers/HeapTrackingContainer/HeapTrackingContainer';
import {FlashQueryParamProvider} from 'utils/flashQueryParam';
import {setMastheadHeightToDefault} from 'utils/mastheadHeight';
import ownHistory from 'utils/ownHistory';
import {ProviderForUseClientSideRoutingHasOccurred} from 'utils/useClientSideRoutingHasOccurred';
import NextUrlProvider from 'utils/useNextUrl/useNextUrl';

import newselaMuiTheme from './newselaMuiTheme';
import RoutesContainer from './RoutesContainer';
import {updateReferrer} from '../../utils/sessionActivity';
import EventTrackingProviderSnitch from '../../utils/snitch/EventTrackingProviderSnitch';
import PageViewTrigger from '../components/PageViewTrigger/PageViewTrigger';
import GoogleTagManagerScript from '../gtmIntegration/GoogleTagManagerScript';
import {requestCurrentUser} from '../modules/actions/userActions';
import {requestUserExperimentSegments} from '../modules/actions/userExperimentSegmentsActions';
import configureStore from '../store/configureStore';
// import polyfill for IntersectionObserver
// https://github.com/w3c/IntersectionObserver/tree/master/polyfill
import 'intersection-observer';

import '../styles/global.scss';

const queryClient = new QueryClient();

const store = configureStore();
store.dispatch(requestCurrentUser());
store.dispatch(getLikedContent());
store.dispatch(fetchCollections());
store.dispatch(requestUserExperimentSegments());

lscache.enableWarnings(true);

// track inner height, see: https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
const updateInnerHeight = () => {
  document.documentElement.style.setProperty('--inner_height', `${window.innerHeight}px`);
};
updateInnerHeight();
window.addEventListener('resize', updateInnerHeight);

// Regarding the masthead height, it currently remains necessary to:
// 1. Be able to access it from CSS and / or JS
// 2. Overwrite it at runtime
// Given those constraints, to universalize the masthead height, we
// are defining it as a JS constant, then initializing it here.
setMastheadHeightToDefault();

/*
  <App> is the main entry point for all of the Three-Oh pages.
  App sets up the Redux Store, MUI Theme, and top-level client routes.
 */

const angelouTerms = {
  BookmarkButton: {
    inactiveScreenReaderText: 'Save content',
    activeScreenReaderText: 'Remove from your content'
  },
  BradburyBadge: {
    text: 'Advisory'
  }
};
class App extends React.Component {
  /**
   * in case we do server side rendering we need to have
   * a window object to add a listener to
   */
  componentDidMount() {
    store.dispatch(setScreenSize(window.innerWidth));
    window.addEventListener('resize', () => {
      store.dispatch(setScreenSize(window.innerWidth));
    });

    /**
     * add accessibility blue ring focus if user is tabbing around
     */
    const handleFirstTab = (e) => {
      if (e.keyCode === 9 || e.keyCode === 13 || e.keyCode === 27) {
        document.body.classList.add('user-is-tabbing');

        window.removeEventListener('keydown', handleFirstTab);
        window.addEventListener('mousedown', handleFirstClick);// eslint-disable-line no-use-before-define
      }
    };

    const handleFirstClick = () => {
      document.body.classList.remove('user-is-tabbing');

      window.removeEventListener('mousedown', handleFirstClick);
      window.addEventListener('keydown', handleFirstTab);
    };

    window.addEventListener('keydown', handleFirstTab);
    window.addEventListener('mousedown', handleFirstClick);
    updateReferrer();
  }

  render() {
    const contentId = 'contentContainer';
    return (
      <Provider store={store}>
        <MuiThemeProvider muiTheme={newselaMuiTheme}>
          <Router history={ownHistory}>
            <QueryClientProvider client={queryClient}>
              <QueryParamProvider ReactRouterRoute={Route}>
                <FlashQueryParamProvider>
                  <ProviderForUseClientSideRoutingHasOccurred>
                    <NextUrlProvider>
                      <EventTrackingProviderSnitch>
                        <PageViewTrigger />
                        <GoogleTagManagerScript />
                        <AngelouTermSheet terms={angelouTerms}>
                          <SalesforceChatSupport>
                            <div>
                              <GlobalLayout
                                history={ownHistory}
                                skipToId={contentId}
                                main={<RoutesContainer contentId={contentId} />}
                              />
                              {/*
                              These dialogs aren't specific to any individual route.
                              We're rendering them conditionally because we want to
                              be able to bypass them in integration tests, as often
                              we'll get stuck in a state where we can't test
                              functionality because the modal is blocking elements
                              we're pointing Selenium at.
                            */}
                              {!window || window.INCLUDE_MARKETING_MODALS ?
                                <>
                                  <AssignButtonModal
                                    lazy
                                    params={{include_drafts: true, excluded_roles: 'district_admin,principal'}}
                                  />
                                </>
                                : null}
                            </div>
                          </SalesforceChatSupport>
                        </AngelouTermSheet>
                      </EventTrackingProviderSnitch>
                    </NextUrlProvider>
                  </ProviderForUseClientSideRoutingHasOccurred>
                </FlashQueryParamProvider>
              </QueryParamProvider>
              <HeapTrackingContainer />
              <AppcuesTrackingContainer />
            </QueryClientProvider>
          </Router>
        </MuiThemeProvider>
      </Provider>
    );
  }
}

ReactDOM.render(
  <App />,
  document.getElementById('app'),
);
